/**
 * SDR Stock Manager - Search, Token Matching & Text Utilities
 * @module utils/search
 */

import { formatBoxContentsDetail } from './box-contents.js';

/**
 * Computes or retrieves the memoized product group key for an inventory item.
 * @param {import('../types/index.js').InventoryItem|Object} item
 * @returns {string} `${name}||${brand}||${size}||${unit}` (lowercased)
 */
export function getItemGroupKey(item) {
  if (!item) return '';
  if (item._groupKey !== undefined) return item._groupKey;

  const key = `${String(item.name || '').trim().toLowerCase()}||${String(item.brand || '').trim().toLowerCase()}||${String(item.size || '').trim().toLowerCase()}||${String(item.unit || '').trim().toLowerCase()}`;
  item._groupKey = key;
  return key;
}

/**
 * Splits a search string into lowercase whitespace-delimited tokens.
 * @param {string|null|undefined} query
 * @returns {string[]}
 */
export function parseSearchTokens(query) {
  if (!query || typeof query !== 'string') return [];
  return query
    .toLowerCase()
    .trim()
    .split(/\s+/)
    .filter(token => token.length > 0);
}

/**
 * Tests if searchable text contains ALL search tokens (multi-token AND match).
 * @param {string|null|undefined} searchableText
 * @param {string[]} tokens
 * @returns {boolean}
 */
export function matchesSearchTokens(searchableText, tokens) {
  if (!tokens || tokens.length === 0) return true;
  if (!searchableText) return false;

  const text = typeof searchableText === 'string'
    ? searchableText.toLowerCase()
    : String(searchableText).toLowerCase();

  return tokens.every(token => text.includes(token));
}

/**
 * Tests if searchable text matches a raw search query string.
 * @param {string|null|undefined} searchableText
 * @param {string|null|undefined} query
 * @returns {boolean}
 */
export function matchesSearchQuery(searchableText, query) {
  const tokens = parseSearchTokens(query);
  return matchesSearchTokens(searchableText, tokens);
}

/**
 * Computes or retrieves the memoized searchable text string for an inventory item.
 * @param {import('../types/index.js').InventoryItem|Object} item
 * @returns {string} Lowercase searchable text
 */
export function getItemSearchToken(item) {
  if (!item) return '';
  if (item._searchToken !== undefined) return item._searchToken;

  const parts = [
    item.name,
    item.brand,
    item.size,
    item.unit,
    item.lot,
    item.location,
    item.notes,
    formatBoxContentsDetail(item.boxContents)
  ];

  const token = parts.filter(Boolean).join(' ').toLowerCase();
  item._searchToken = token;
  return token;
}

/**
 * Computes or retrieves the memoized searchable text string for a product group.
 * @param {import('../types/index.js').ProductGroup|Object} group
 * @returns {string} Lowercase searchable text
 */
export function getGroupSearchToken(group) {
  if (!group) return '';
  if (group._searchToken !== undefined) return group._searchToken;

  const parts = [
    group.name,
    group.brand,
    group.size,
    group.unit,
    group.notes
  ];

  const token = parts.filter(Boolean).join(' ').toLowerCase();
  group._searchToken = token;
  return token;
}

/**
 * Safely escapes HTML special characters to prevent XSS.
 * @param {string|null|undefined} str
 * @returns {string}
 */
export function escapeHtml(str) {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

/**
 * Generates a unique timestamped ID with a given prefix.
 * E.g. "item_1740848810_a1b2c3d4e", "tx_1740848810_a1b2c3d4e"
 * @param {string} [prefix='id']
 * @returns {string}
 */
export function generateId(prefix = 'id') {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
